<template>
  <Teleport to="body">
    <cl-modal
      :visible="showModal"
      @on-close="onCloseModal"
      headerLabel="Add Sender Domain"
    >
      <form @submit.prevent="submit" autocomplete="off">
        <div>
          <cl-form-group>
            <cl-form-label label-for="domain">
              {{ $t("Sender Domain") }}
            </cl-form-label>
            <cl-form-input
              data-lpignore="true"
              id="domain"
              name="domain"
              v-model:value="form.domain"
              :placeholder="$t('Enter Domain')"
              :state="setInputState(v$.form.domain)"
              @on-blur="v$.form.domain.$touch"
            />
            <span
              class="tw-mt-1 tw-block tw-text-sm tw-text-danger-500"
              v-if="v$.form.domain.$error"
              data-test-id="validation-required-field"
            >
              {{ $t("This field is a required field") }}
            </span>
          </cl-form-group>
        </div>
        <div class="tw-mb-3 tw-rounded-md tw-bg-light tw-p-2">
          <cl-checkbox
            id="subdomains"
            testId="modal-domain-subdomains"
            v-model:model-value="form.subdomains"
          >
            {{ $t("Include Subdomains") }}
          </cl-checkbox>
        </div>
        <div>
          <cl-form-group>
            <cl-form-label label-for="comment">
              {{ $t("Comments (optional)") }}
            </cl-form-label>
            <cl-form-textarea
              id="textarea"
              v-model:value="form.comment"
              rows="3"
              max-rows="6"
            ></cl-form-textarea>
          </cl-form-group>
        </div>
      </form>

      <template v-slot:[`footer.decline`]>
        <cl-button
          :disabled="fetching"
          @on-click="closeModal"
          variant="link-secondary"
        >
          <span>{{ $t("Cancel") }}</span>
        </cl-button>
      </template>
      <template v-slot:[`footer.accept`]>
        <cl-button
          :variant="'secondary'"
          @on-click="submit"
          data-test-id="modal-sender-domain-add-button"
        >
          <span v-if="!fetching">{{ this.$t("Add") }}</span>
          <cl-spinner v-else :size="'small'" />
        </cl-button>
      </template>
    </cl-modal>
  </Teleport>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { useFocusElement } from "@/composables/useFocusElement";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";

const DEFAULT_FORM = Object.freeze({
  domain: null,
  comment: null,
  subdomains: false,
});

export default {
  setup() {
    const { focusElementById } = useFocusElement();
    return { v$: useVuelidate(), focusElementById };
  },
  validations() {
    return {
      form: {
        domain: {
          required,
        },
      },
    };
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    tier: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      form: {
        ...DEFAULT_FORM,
      },
    };
  },
  computed: {
    ...mapGetters("modal", ["showModal"]),
  },
  watch: {
    showModal: {
      handler() {
        this.focusElementById("domain");
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions("geoBlockingExemptions", ["createGeoBlockingExemption"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("modal", ["closeModal"]),
    setInputState(input) {
      return input.$dirty ? !input.$error : null;
    },
    onCloseModal() {
      this.closeModal();
      this.form = { ...DEFAULT_FORM };
      this.v$.$reset();
    },
    async submit() {
      const isValid = await this.v$.form.$validate();

      if (!isValid) {
        return;
      }

      try {
        await this.createGeoBlockingExemption({
          tier: this.$props.tier,
          id: this.$props.id,
          exemption: this.form,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Domain has been added"),
          duration: 2000,
          variant: "success",
        });
        this.onCloseModal();
      } catch (_err) {
        // stub
      }
    },
  },
};
</script>
